import React from 'react';
import PropTypes from 'prop-types';
import { ValidatorForm } from 'react-material-ui-form-validator';

import Constants from "../utils/Constants";
import StandardFormLayout from './StandardFormLayout';
import LogoPrimary from "../assets/detect-v2-left-aligned.svg";
import LogoSecondaryLight from "../assets/detect-logo-dark-color.png";
import { states } from '../data/states';

import {
	makeStyles,
	Button, Typography,
	Grid,
	MenuItem
} from '@material-ui/core';
import LabeledTextValidator from './LabeledTextValidator';
import LabeledSelect from './LabeledSelect';

const topLogoSize = { width: 250, height: 50 };

const styles = makeStyles(theme => ({
	submitBtnWrapper: {
		display: "flex",
		justifyContent: "center"
	},

	helperText: {
		display: "block"
	},

	container: {
		margin: "0px 120px 20px 80px"
	},

	titleSelect: {
		textAlign: "left",
		minWidth: theme.spacing(22)
	},

	primary: {
		width: topLogoSize.width,
		height: topLogoSize.height,
		backgroundSize: topLogoSize.width,
		background: 'url("' + LogoPrimary + '") no-repeat center',
		paddingBottom: 50,
	},

	secondaryLight: {
		width: topLogoSize.width,
		height: topLogoSize.height,
		backgroundSize: `200px`,
		background: 'url("' + LogoSecondaryLight + '") no-repeat center',
		marginTop: 30,
	},

	heading: {
		fontSize: 30,
		fontFamily: "Merriweather",
		fontWeight: 400,
		lineHeight: "38px",
	},

	buttonLink: {
		borderColor: '#99CBD2',
	},

	bold: {
		fontWeight: 600
	}
}));

const detectBrowser = () => {
	const userAgent = navigator.userAgent;
	if (userAgent.includes("Firefox")) return "Firefox";
	if (userAgent.includes("Edg")) return "Edge";
	if (userAgent.includes("Chrome") && !userAgent.includes("Edg")) return "Chrome";
	if (userAgent.includes("Safari") && !userAgent.includes("Chrome")) return "Safari";
	if (userAgent.includes("Opera") || userAgent.includes("OPR")) return "Opera";
	return "Unknown Browser";
};

const getIpInfo = async () => {
	// Only run in browser environment, not during build
	if (typeof window === 'undefined') {
		return null;
	}

	try {
		const response = await fetch(`https://ipinfo.io/json?token=${process.env.REACT_APP_IPINFO_TOKEN}`);
		const data = await response.json();

		return {
			ip: data.ip,
			city: data.city,
			region: data.region,
			country: data.country,
			location: data.loc,
			timezone: data.timezone,
			org: data.org,
		};
	} catch (error) {
		console.error("Failed to fetch IP info:", error);
		return null;
	}
};

const gatherMetaInfo = async () => {
	const ipinfo = await getIpInfo();
	const browser = detectBrowser();

	return {
		ipinfo,
		browser,
	};
};

function SignupForm(props) {
	const account_verticals = ["K12"]
	// Convert the submitted form even to an object of values instead
	const handleSubmit = async (event) => {
		let form = event.target;
		let values = {
			id: props.formValues.id,
		};
		const meta = await gatherMetaInfo();
		Object.keys(form.elements).map(e => {
			// Don't send if element not named
			if (form.elements[e].nodeName && form.elements[e].name) {
				values[form.elements[e].name] = form.elements[e].value;
			}

			return e;
		});

		const payload = { ...values, meta }
		console.log(payload);
		props.handleSubmit(payload);
	};

	let classes = styles(),
		{ handleChange, formValues, dataLoading, timezones, allDistricts } = props,
		{ accountName, username, firstName, lastName, title, timezone, state, districts } = formValues;

	return (
		<div className={classes.container}>
			<div className={classes.primary} alt="" />
			<Typography className={classes.heading}>Sign up to your account</Typography>
			<ValidatorForm
				method="post"
				action="./"
				debounceTime={500}
				onSubmit={handleSubmit}>
				<StandardFormLayout>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<LabeledTextValidator
								fullWidth
								id="accountName"
								variant="outlined"
								label={<>
									Account Name <span style={{ color: 'red' }}>*</span>
								</>}
								name="accountName"
								helperText="Account name for register"
								margin="normal"
								value={accountName || ''}
								inputProps={{
									autoCapitalize: "off",
									autoComplete: "accountName"
								}}
								onChange={handleChange}
								validators={['required']}
								errorMessages={[Constants.ERROR_MESSAGES['required']]} />
						</Grid>
						<Grid item xs={6}>
							<LabeledSelect
								fullWidth
								isLoading={dataLoading}
								id="vertical"
								name="vertical"
								label={<>
									Account Vertical
								</>}
								helperText="Account vertical"
								value={account_verticals[0]}
								variant="outlined"
								onChange={handleChange}
								className={classes.bold}
								style={{ textAlign: "start" }}
								validators={['required']}
								disabled
								errorMessages={[Constants.ERROR_MESSAGES['required']]}>
								{account_verticals.map(vertical =>
									<MenuItem key={vertical} value={vertical}>{vertical}</MenuItem>
								)}
							</LabeledSelect>
						</Grid>
					</Grid>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<LabeledSelect
								fullWidth
								isLoading={dataLoading}
								id="state"
								name="state"
								label={<>
									State <span style={{ color: 'red' }}>*</span>
								</>}
								helperText="State"
								value={state}
								variant="outlined"
								onChange={handleChange}
								style={{ textAlign: "start" }}
								validators={['required']}
								errorMessages={[Constants.ERROR_MESSAGES['required']]}>
								{states.map(state =>
									<MenuItem key={state.value} value={state.value}>{state.label}</MenuItem>
								)}
							</LabeledSelect>
						</Grid>
						<Grid item xs={6}>
							<LabeledSelect
								fullWidth
								isLoading={dataLoading}
								id="districts"
								name="districts"
								label={<>
									School Districts <span style={{ color: 'red' }}>*</span>
								</>}
								helperText="Select the districts"
								value={districts || []}
								disabled={!state}
								// multiple select props
								multiple
								includeSelectAll
								labelAttr="name"
								valueAttr="id"
								allOptions={allDistricts}
								onChange={handleChange}
								validators={['required']}
								errorMessages={[Constants.ERROR_MESSAGES['required']]}
								variant="outlined" />
						</Grid>
					</Grid>
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<LabeledTextValidator
								fullWidth
								autoFocus
								id="username"
								variant="outlined"
								label={<>
									User Name <span style={{ color: 'red' }}>*</span>
								</>}
								name="username"
								helperText="Email address or username for login"
								margin="normal"
								value={username || ''}
								inputProps={{
									autoCapitalize: "off",
									autoComplete: "username"
								}}
								onChange={handleChange}
								validators={['required', 'isEmail']}
								errorMessages={[Constants.ERROR_MESSAGES['required'], Constants.ERROR_MESSAGES['isEmail']]} />
						</Grid>
						<Grid item xs={6}>
							<LabeledTextValidator
								fullWidth
								id="title"
								label="Job Title (Optional)"
								name="title"
								helperText="User's job title"
								value={title || ''}
								margin="normal"
								onChange={handleChange}
								variant="outlined" />
						</Grid>
					</Grid>

					<Grid container spacing={2}>
						<Grid item xs={6}>
							<LabeledTextValidator
								fullWidth
								id="firstName"
								variant="outlined"
								label={<>
									First Name <span style={{ color: 'red' }}>*</span>
								</>}
								name="firstName"
								helperText="Firstname for register"
								margin="normal"
								value={firstName || ''}
								inputProps={{
									autoCapitalize: "off",
									autoComplete: "firstName"
								}}
								onChange={handleChange}
								validators={['required']}
								errorMessages={[Constants.ERROR_MESSAGES['required']]} />
						</Grid>
						<Grid item xs={6}>
							<LabeledTextValidator
								fullWidth
								id="lastName"
								variant="outlined"
								label={<>
									Last Name <span style={{ color: 'red' }}>*</span>
								</>}
								name="lastName"
								helperText="Lastname for register"
								margin="normal"
								value={lastName || ''}
								inputProps={{
									autoCapitalize: "off",
									autoComplete: "lastName"
								}}
								onChange={handleChange}
								validators={['required']}
								errorMessages={[Constants.ERROR_MESSAGES['required']]} />
						</Grid>
					</Grid>

					<Grid container spacing={2}>
						<Grid item xs={6}>
							<LabeledSelect
								fullWidth
								isLoading={dataLoading}
								id="timezone"
								name="timezone"
								label={<>
									Timezone <span style={{ color: 'red' }}>*</span>
								</>}
								helperText="User's timezone"
								value={timezone}
								variant="outlined"
								onChange={handleChange}
								style={{ textAlign: "start" }}
								validators={['required']}
								errorMessages={[Constants.ERROR_MESSAGES['required']]}>
								{timezones.map(timezone =>
									<MenuItem key={timezone} value={timezone}>{timezone}</MenuItem>
								)}
							</LabeledSelect>
						</Grid>
					</Grid>

					<Button
						fullWidth
						id="submit"
						type="submit"
						variant="contained"
						color="primary">Register</Button>

				</StandardFormLayout>
			</ValidatorForm>
		</div>
	);
}

SignupForm.propTypes = {
	handleChange: PropTypes.func.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	handleForgotToggle: PropTypes.func,
	formValues: PropTypes.object.isRequired,
	dataLoading: PropTypes.bool,
	timezones: PropTypes.array.isRequired,
};

export default SignupForm;
